import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import useTranslate from '../../../../lib/hooks/use-translate'
import { CseContactInput } from '../../../../lib/types/models/cse/cse-contact'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { PHONE_NUMBER_SCHEMA } from '../../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import ControlledSwitch from '../../../elements/controlled-switch'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../../elements/inputs/controlled-phone-input'
import ValidationButtons from '../../../elements/validation-buttons'

export const formSchema = (
  t: TFunction<AvailableLanguages, undefined>
): ZodType<CseContactInput> =>
  z.object({
    firstName: createStringSchema('Prénom'),
    lastName: createStringSchema('Nom'),
    position: createStringSchema('Poste'),
    email: createStringSchema('Email', { isEmail: true }),
    phone: PHONE_NUMBER_SCHEMA(t).nullish(),
    fixedPhone: PHONE_NUMBER_SCHEMA(t).nullish(),
    canReceiveEmails: z.boolean()
  })

type Props = BaseFormProps<CseContactInput, true> & {
  className?: string
}

const CseContactForm = ({ className, defaultValues, ...validationButtonProps }: Props) => {
  const form = useForm<CseContactInput>({
    resolver: zodResolver(formSchema(useTranslate().t)),
    defaultValues,
    mode: FORM_MODE
  })
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex gap-4 flex-col', className)}>
          <FormField
            control={form.control}
            name={'firstName'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput required {...field} label={'Prénom'} type={'text'} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'lastName'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput required {...field} label={'Nom'} type={'text'} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'position'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput required {...field} label={'Fonction'} type={'text'} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'email'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput required {...field} label={'Email'} type={'email'} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'phone'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledPhoneInput
                    {...field}
                    label={'Numéro de téléphone'}
                    defaultCountry={'FR'}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'fixedPhone'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledPhoneInput
                    {...field}
                    label={'Numéro de téléphone fixe'}
                    defaultCountry={'FR'}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'canReceiveEmails'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSwitch
                    {...field}
                    switchVariant="primary"
                    label="Peut recevoir des emails ?"
                    variant={'label-medium'}
                    color={tailwindColors.black.DEFAULT}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default CseContactForm
